import { Outlet, NavLink, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import {
  FileText,
  History,
  Settings,
  Wand2,
  ExternalLink,
  Image,
  Mail,
  Palette,
  Globe,
  Server,
  ChevronDown,
  Menu,
  X,
  ArrowLeft,
  LayoutDashboard,
  BarChart3,
  Layout as LayoutIcon,
} from 'lucide-react';
import { UserButton } from '@clerk/clerk-react';
import { settings } from '../lib/api';
import { useSite } from '../contexts/SiteContext';
import GlobalAIChat from './GlobalAIChat';
import OnboardingChecklist from './OnboardingChecklist';
import { useOnboardingProgress } from '../hooks/useOnboardingProgress';

// Navigation when viewing all sites
const allSitesNavigation = [
  { name: 'All Sites', to: '/sites', icon: Server },
];

// Navigation when viewing a specific site
const siteNavigation = [
  { name: 'Overview', to: '/overview', icon: LayoutDashboard },
  { name: 'Domains', to: '/domains', icon: Globe },
  { name: 'Pages', to: '/pages', icon: FileText },
  { name: 'Templates', to: '/templates', icon: LayoutIcon },
  { name: 'Media', to: '/media', icon: Image },
  { name: 'Contact', to: '/contact', icon: Mail },
  { name: 'Design', to: '/design', icon: Palette },
  { name: 'SEO & Analytics', to: '/seo', icon: BarChart3 },
  { name: 'Revisions', to: '/revisions', icon: History },
  { name: 'Site Settings', to: '/settings', icon: Settings },
];

export default function Layout() {
  const { selectedSite, sites: sitesList, setSelectedSite, isLoading } = useSite();
  const location = useLocation();
  const isDev = import.meta.env.MODE === 'development';
  const { items: checklistItems, showChecklist, handleDismiss } = useOnboardingProgress();

  // Load sidebar state from localStorage, default to expanded
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved ? JSON.parse(saved) : false;
  });

  // Mobile menu state
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Save sidebar state to localStorage
  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', JSON.stringify(sidebarCollapsed));
  }, [sidebarCollapsed]);

  // Close mobile menu when clicking outside
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  // Determine if we're in the "all sites" view or a specific site view
  const isAllSitesView = location.pathname === '/sites';
  const navigation = isAllSitesView ? allSitesNavigation : siteNavigation;

  const subdomain = selectedSite?.subdomain || settings.getSubdomain();
  const siteUrl = subdomain
    ? isDev
      ? `http://localhost:8788?subdomain=${subdomain}`
      : `https://${subdomain}.imerkar.com`
    : null;
  const editUrl = siteUrl ? `${siteUrl}?edit=true` : null;

  return (
    <div className="min-h-screen bg-gray-50 overflow-x-hidden relative">
      {/* Mobile Header - Only visible on mobile */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-white border-b border-gray-200 z-30 flex items-center justify-between px-4">
        <button
          onClick={() => setMobileMenuOpen(true)}
          className="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors"
          aria-label="Open menu"
        >
          <Menu className="w-6 h-6 text-gray-700" />
        </button>
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-purple-600 rounded-lg flex items-center justify-center">
            <Wand2 className="w-5 h-5 text-white" />
          </div>
          <h1 className="text-lg font-bold text-gray-900">AI Builder</h1>
        </div>
        <div className="w-10">
          {import.meta.env.VITE_CLERK_PUBLISHABLE_KEY && <UserButton />}
        </div>
      </div>

      {/* Mobile Menu Backdrop */}
      {mobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar - Desktop: always visible, Mobile: drawer */}
      <div className={`
        w-64 h-dvh max-h-dvh
        bg-white border-r border-gray-200 flex flex-col transition-all duration-300 relative
        ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
        ${sidebarCollapsed ? 'lg:-translate-x-full' : 'lg:translate-x-0'}
        fixed lg:fixed top-0 bottom-0 left-0 z-50 lg:z-40
      `}>
        {/* Desktop Close Button - Inside sidebar when open */}
        {!sidebarCollapsed && (
          <button
            onClick={() => setSidebarCollapsed(true)}
            className="hidden lg:flex absolute -right-3 top-6 z-10 w-6 h-6 bg-white border border-gray-200 rounded-full shadow-md items-center justify-center hover:bg-gray-50 transition-colors"
            aria-label="Collapse sidebar"
          >
            <X className="w-4 h-4 text-gray-600" />
          </button>
        )}

        {/* Scrollable content wrapper - takes remaining space */}
        <div className="flex-1 overflow-y-auto min-h-0">
          {/* Mobile Header - Minimal icon + close button */}
          <div className="lg:hidden flex items-center justify-between px-4 py-3 border-b border-gray-200">
            <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
              <Wand2 className="w-5 h-5 text-white" />
            </div>
            <button
              onClick={() => setMobileMenuOpen(false)}
              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors"
              aria-label="Close menu"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>
          </div>

          {/* Desktop Logo */}
          <div className="hidden lg:block p-6 border-b border-gray-200">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-purple-600 rounded-lg flex items-center justify-center flex-shrink-0">
                <Wand2 className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-gray-900">AI Builder</h1>
              </div>
            </div>
          </div>

          {/* Site Selector - Only show when NOT in all sites view */}
          {!isAllSitesView && !isLoading && sitesList.length > 0 && (
            <div className="px-3 py-3 lg:px-4 lg:py-3 bg-gray-50 lg:bg-transparent border-b border-gray-200">
              <label className="text-[10px] lg:text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5 lg:mb-2 block">
                Current Site
              </label>
              <div className="relative">
                <select
                  value={selectedSite?.id || ''}
                  onChange={(e) => {
                    const site = sitesList.find((s: any) => s.id === e.target.value);
                    if (site) setSelectedSite(site);
                  }}
                  className="w-full bg-white border border-gray-300 rounded-lg px-2 py-1.5 lg:px-3 lg:py-2 pr-8 text-sm font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 appearance-none cursor-pointer"
                >
                  {sitesList.map((site: any) => (
                    <option key={site.id} value={site.id}>
                      {site.name} {site.isPrimary ? '⭐' : ''}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
              </div>
            </div>
          )}

          {/* Back to All Sites - Show when NOT in all sites view */}
          {!isAllSitesView && (
            <div className="px-3 pt-3 pb-2 lg:px-4 lg:pt-4 lg:pb-2">
              <NavLink
                to="/sites"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Back to All Sites</span>
              </NavLink>
            </div>
          )}

          {/* Navigation - Tighter spacing on mobile with top padding */}
          <nav className="px-3 pt-2 pb-3 lg:p-4 space-y-0.5 lg:space-y-1">
            {navigation.map((item) => (
              <NavLink
                key={item.name}
                to={item.to}
                onClick={() => setMobileMenuOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 lg:py-2 rounded-lg transition-colors ${
                    isActive
                      ? 'bg-primary-50 text-primary-600 font-medium'
                      : 'text-gray-700 hover:bg-gray-50 active:bg-gray-100'
                  }`
                }
              >
                <item.icon className="w-5 h-5 flex-shrink-0" />
                <span>{item.name}</span>
              </NavLink>
            ))}
          </nav>

          {/* Site List - Show when in all sites view */}
          {isAllSitesView && !isLoading && sitesList.length > 0 && (
            <div className="px-3 lg:px-4 pb-3 lg:pb-4">
              <div className="border-t border-gray-200 pt-3 lg:pt-4">
                <h3 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2 px-3">
                  Your Sites
                </h3>
                <div className="space-y-0.5 lg:space-y-1">
                  {sitesList.map((site: any) => (
                    <button
                      key={site.id}
                      onClick={() => {
                        setSelectedSite(site);
                        localStorage.setItem('siteId', site.id);
                        window.location.href = '/overview';
                      }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 active:bg-gray-100 rounded-lg transition-colors text-left"
                    >
                      <Globe className="w-4 h-4 flex-shrink-0 text-gray-400" />
                      <span className="flex-1 truncate">{site.name}</span>
                      {site.isPrimary && (
                        <span className="text-xs">⭐</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Onboarding Checklist - Show above footer when relevant */}
          {showChecklist && !isAllSitesView && (
            <div className="px-3 lg:px-4 pb-3 lg:pb-4">
              <OnboardingChecklist items={checklistItems} onDismiss={handleDismiss} />
            </div>
          )}
        </div>

        {/* Footer - Part of flex layout, won't scroll */}
        <div className="flex-shrink-0 border-t border-gray-200 bg-white">
          <div className="p-3 lg:p-4 space-y-2 lg:space-y-3">
            {/* View Live Site - Only show when NOT in all sites view */}
            {!isAllSitesView && editUrl && (
              <a
                href={editUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full btn btn-primary flex items-center justify-center gap-2"
              >
                <ExternalLink className="w-4 h-4" />
                View Live Site
              </a>
            )}
            {/* Profile - Desktop only (mobile has it in fixed header) */}
            {import.meta.env.VITE_CLERK_PUBLISHABLE_KEY && (
              <div className="hidden lg:flex items-center gap-3">
                <UserButton />
                <div className="text-sm">
                  <div className="font-medium text-gray-900">Account</div>
                  <div className="text-gray-500 text-xs">Dashboard</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className={`min-h-screen overflow-auto pt-16 lg:pt-0 transition-all duration-300 absolute inset-0 ${
        sidebarCollapsed ? 'lg:left-0' : 'lg:left-64'
      }`}>
        <Outlet />
      </div>

      {/* Floating Menu Button - Desktop only, when sidebar collapsed */}
      {sidebarCollapsed && (
        <button
          onClick={() => setSidebarCollapsed(false)}
          className="hidden lg:flex fixed top-6 left-6 z-30 w-10 h-10 bg-white border border-gray-200 rounded-lg shadow-md items-center justify-center hover:bg-gray-50 transition-all hover:shadow-lg"
          aria-label="Open sidebar"
        >
          <Menu className="w-5 h-5 text-gray-700" />
        </button>
      )}

      {/* Global AI Chat */}
      <GlobalAIChat />
    </div>
  );
}
